import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Calendar as CalendarIcon,
  Clock,
  User,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  List,
  Grid,
  Eye,
  MessageSquare,
  Filter,
  Plus
} from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
  rescheduled: 'bg-purple-100 text-purple-700'
};

const statusLabels = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  cancelled: 'Annulé',
  completed: 'Terminé',
  rescheduled: 'Reporté'
};

const BookingsCalendarPage = () => {
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const { lang } = useLanguage();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, total: 0 });
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Create booking state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [newBooking, setNewBooking] = useState({
    appointment_type_id: '',
    proposed_date: '',
    proposed_time: '',
    full_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentMonth, statusFilter]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get(`${API_URL}/booking/types`);
        setAppointmentTypes(res.data);
      } catch (e) { console.error(e); }
    };
    fetchTypes();
  }, []);

  const fetchSlots = async (date, typeId) => {
    if (!date || !typeId) return;
    try {
      setLoadingSlots(true);
      const res = await axios.get(`${API_URL}/calendar/availability?date=${date}&appointment_type_id=${typeId}`);
      setAvailableSlots(res.data.available_slots || []);
    } catch (e) { console.error(e); }
    finally { setLoadingSlots(false); }
  };

  const handleCreateBooking = async () => {
    if (!newBooking.appointment_type_id || !newBooking.proposed_date || !newBooking.proposed_time || !newBooking.full_name || !newBooking.email || !newBooking.phone) return;
    try {
      setCreating(true);
      await axios.post(`${API_URL}/booking/request`, newBooking);
      setCreateDialogOpen(false);
      setNewBooking({ appointment_type_id: '', proposed_date: '', proposed_time: '', full_name: '', email: '', phone: '', message: '' });
      setAvailableSlots([]);
      await fetchData();
    } catch (error) {
      console.error(error);
      alert('Erreur lors de la création');
    } finally { setCreating(false); }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      // Fetch calendar events
      const eventsRes = await axios.get(`${API_URL}/calendar/events?start_date=${startDate}&end_date=${endDate}&types=bookings`);
      setEvents(eventsRes.data);
      
      // Fetch bookings list
      const bookingsRes = await axios.get(`${API_URL}/booking/requests${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`);
      setBookings(bookingsRes.data.bookings);
      setStats({
        pending: bookingsRes.data.pending_count,
        confirmed: bookingsRes.data.confirmed_count,
        total: bookingsRes.data.total
      });
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status, confirmedDate = null, confirmedTime = null, notes = null) => {
    try {
      setUpdating(true);
      
      await axios.patch(`${API_URL}/booking/requests/${bookingId}/status`, {
        status,
        confirmed_date: confirmedDate,
        confirmed_time: confirmedTime,
        admin_notes: notes
      });
      
      await fetchData();
      setDialogOpen(false);
      setSelectedBooking(null);
      
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => event.start.startsWith(dateStr));
  };

  // Calendar view
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Pad days to start on Monday
  const startDayOfWeek = monthStart.getDay();
  const paddedDays = [];
  for (let i = 0; i < (startDayOfWeek === 0 ? 6 : startDayOfWeek - 1); i++) {
    paddedDays.push(null);
  }

  return (
    <div className="space-y-6" data-testid="bookings-calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            Calendrier des rendez-vous
          </h1>
          <p className="text-gray-500">Gérez vos demandes de rendez-vous</p>
        </div>
        
        {/* Stats */}
        <div className="flex gap-4 items-center">
          <div className="bg-yellow-50 px-4 py-2 rounded-lg">
            <span className="text-yellow-700 font-bold">{stats.pending}</span>
            <span className="text-yellow-600 text-sm ml-1">en attente</span>
          </div>
          <div className="bg-green-50 px-4 py-2 rounded-lg">
            <span className="text-green-700 font-bold">{stats.confirmed}</span>
            <span className="text-green-600 text-sm ml-1">confirmé(s)</span>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-booking-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau RDV
          </Button>
        </div>
      </div>

      {/* View Toggle & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <Grid className="w-4 h-4 mr-1" />
            Calendrier
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4 mr-1" />
            Liste
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_statuses', lang)}</SelectItem>
              <SelectItem value="pending">{t('status_pending', lang)}</SelectItem>
              <SelectItem value="confirmed">Confirmé</SelectItem>
              <SelectItem value="cancelled">Annulé</SelectItem>
              <SelectItem value="completed">{t('status_completed', lang)}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : viewMode === 'calendar' ? (
        /* Calendar View */
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading">
              {format(currentMonth, 'MMMM yyyy', { locale: fr })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
                Aujourd'hui
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Days header */}
            <div className="grid grid-cols-7 mb-2">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Padding for days before month start */}
              {paddedDays.map((_, i) => (
                <div key={`pad-${i}`} className="min-h-24 bg-gray-50 rounded-lg" />
              ))}
              
              {/* Actual days */}
              {daysInMonth.map(day => {
                const dayEvents = getEventsForDay(day);
                const isCurrentDay = isToday(day);
                
                return (
                  <div
                    key={day.toString()}
                    className={`min-h-24 p-2 rounded-lg border ${
                      isCurrentDay ? 'border-primary bg-primary/5' : 'border-gray-100'
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-primary' : 'text-gray-700'}`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <button
                          key={event.id}
                          onClick={() => {
                            const booking = bookings.find(b => b.id === event.id);
                            if (booking) {
                              setSelectedBooking(booking);
                              setDialogOpen(true);
                            }
                          }}
                          className="w-full text-left p-1 rounded text-xs truncate"
                          style={{ backgroundColor: event.color + '20', color: event.color }}
                        >
                          {event.start.split('T')[1].slice(0, 5)} - {event.metadata?.full_name?.split(' ')[0]}
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <p className="text-xs text-gray-400 pl-1">+{dayEvents.length - 3} autre(s)</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* List View */
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <div className="divide-y">
              {bookings.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun rendez-vous trouvé</p>
                </div>
              ) : (
                bookings.map(booking => (
                  <div
                    key={booking.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedBooking(booking);
                      setDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: booking.appointment_type_color + '20' }}
                      >
                        <CalendarIcon className="w-6 h-6" style={{ color: booking.appointment_type_color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 truncate">{booking.full_name}</h3>
                          <Badge className={statusColors[booking.status]}>
                            {statusLabels[booking.status]}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">{booking.appointment_type_name}</p>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <p className="font-medium text-gray-900">
                          {format(new Date(booking.confirmed_date || booking.proposed_date), 'dd MMM', { locale: fr })}
                        </p>
                        <p className="text-sm text-gray-500">
                          {booking.confirmed_time || booking.proposed_time}
                        </p>
                      </div>
                      
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Booking Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{lang === 'de' ? 'Neuer Termin' : 'Nouveau rendez-vous'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type de rendez-vous *</Label>
              <Select
                value={newBooking.appointment_type_id}
                onValueChange={(v) => {
                  setNewBooking({ ...newBooking, appointment_type_id: v, proposed_time: '' });
                  if (newBooking.proposed_date) fetchSlots(newBooking.proposed_date, v);
                }}
              >
                <SelectTrigger><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                <SelectContent>
                  {appointmentTypes.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.duration_minutes} min)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newBooking.proposed_date}
                onChange={(e) => {
                  setNewBooking({ ...newBooking, proposed_date: e.target.value, proposed_time: '' });
                  if (newBooking.appointment_type_id) fetchSlots(e.target.value, newBooking.appointment_type_id);
                }}
              />
            </div>
            {newBooking.proposed_date && newBooking.appointment_type_id && (
              <div className="space-y-2">
                <Label>Créneau horaire *</Label>
                {loadingSlots ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">{t('bookings_no_slots', lang)}</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                    {availableSlots.map(slot => (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => setNewBooking({ ...newBooking, proposed_time: slot.time })}
                        className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          newBooking.proposed_time === slot.time
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Vollständiger Name *' : 'Nom complet *'}</Label>
                <Input value={newBooking.full_name} onChange={(e) => setNewBooking({ ...newBooking, full_name: e.target.value })} placeholder="Jean Dupont" />
              </div>
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Telefon *' : 'Téléphone *'}</Label>
                <Input value={newBooking.phone} onChange={(e) => setNewBooking({ ...newBooking, phone: e.target.value })} placeholder="+237 6XX..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={newBooking.email} onChange={(e) => setNewBooking({ ...newBooking, email: e.target.value })} placeholder="email@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Message (optionnel)</Label>
              <Textarea value={newBooking.message} onChange={(e) => setNewBooking({ ...newBooking, message: e.target.value })} rows={2} placeholder="Notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('action_cancel', lang)}</Button>
            <Button
              onClick={handleCreateBooking}
              disabled={creating || !newBooking.proposed_time || !newBooking.full_name || !newBooking.email || !newBooking.phone}
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer le RDV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('bookings_details', lang)}</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <Badge className={`${statusColors[selectedBooking.status]} text-sm px-3 py-1`}>
                  {statusLabels[selectedBooking.status]}
                </Badge>
                <span className="text-sm text-gray-500">
                  {selectedBooking.duration_minutes} min
                </span>
              </div>

              {/* Type */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: selectedBooking.appointment_type_color + '20' }}
                >
                  <CalendarIcon className="w-5 h-5" style={{ color: selectedBooking.appointment_type_color }} />
                </div>
                <div>
                  <p className="font-medium">{selectedBooking.appointment_type_name}</p>
                  <p className="text-sm text-gray-500">
                    {format(new Date(selectedBooking.confirmed_date || selectedBooking.proposed_date), 'EEEE dd MMMM yyyy', { locale: fr })}
                    {' à '}{selectedBooking.confirmed_time || selectedBooking.proposed_time}
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{selectedBooking.full_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${selectedBooking.email}`} className="text-primary hover:underline">
                    {selectedBooking.email}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${selectedBooking.phone}`} className="text-primary hover:underline">
                    {selectedBooking.phone}
                  </a>
                </div>
                {selectedBooking.message && (
                  <div className="flex items-start gap-2 text-sm pt-2 border-t">
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                    <p className="text-gray-600">{selectedBooking.message}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <DialogFooter className="flex gap-2 pt-4">
                {selectedBooking.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                      disabled={updating}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Refuser
                    </Button>
                    <Button
                      onClick={() => updateBookingStatus(
                        selectedBooking.id, 
                        'confirmed',
                        selectedBooking.proposed_date,
                        selectedBooking.proposed_time
                      )}
                      disabled={updating}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      {updating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Confirmer
                    </Button>
                  </>
                )}
                {selectedBooking.status === 'confirmed' && (
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'completed')}
                    disabled={updating}
                  >
                    Marquer comme terminé
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsCalendarPage;
